import { Navigate, Outlet, useLocation } from "react-router-dom";

import {
  AsyncBoundary,
  SkeletonRegion,
} from "../../../shared/ui/async-boundary";
import { safeReturnTo } from "./safe-return-to";
import { useSession } from "./session-provider";
import { SessionExpiredError } from "./session-query";

function MemberSessionShell({
  children,
}: {
  readonly children: React.ReactNode;
}) {
  return (
    <div
      className="mx-auto grid min-h-dvh w-full max-w-6xl grid-cols-1 gap-6 px-5 py-8 sm:px-8 lg:grid-cols-[15rem_minmax(0,1fr)]"
    >
      <div
        aria-hidden="true"
        className="hidden min-h-96 rounded-[var(--radius-surface)] bg-[var(--color-surface)] lg:block"
      />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function MemberSessionLoading({ label }: { readonly label: string }) {
  return (
    <MemberSessionShell>
      <SkeletonRegion label={label} />
    </MemberSessionShell>
  );
}

export function MemberGuard() {
  const location = useLocation();
  const session = useSession();

  if (session.isLoggingOut) {
    return <MemberSessionLoading label="Encerrando sua sessão" />;
  }
  if (session.isPending) {
    return <MemberSessionLoading label="Carregando sua conta" />;
  }
  const returnTo = safeReturnTo(
    `${location.pathname}${location.search}`,
  );
  if (session.error instanceof SessionExpiredError) {
    return (
      <Navigate
        replace
        state={{ sessionExpired: true }}
        to={`/entrar?returnTo=${encodeURIComponent(returnTo)}`}
      />
    );
  }
  if (session.isError) {
    return (
      <MemberSessionShell>
        <AsyncBoundary
          error="session"
          headingLevel="h1"
          onRetry={() => session.refetch()}
          retrying={session.isFetching}
          status="error"
        >
          {null}
        </AsyncBoundary>
      </MemberSessionShell>
    );
  }
  if (!session.data?.authenticated) {
    return (
      <Navigate
        replace
        to={`/entrar?returnTo=${encodeURIComponent(returnTo)}`}
      />
    );
  }

  return <Outlet />;
}
